/**
 * Shared Socket.IO transport for the Alveary real-time layer.
 *
 * Architecture:
 *   PostgreSQL NOTIFY → Backend WebSocket → Frontend (this package)
 *
 * Owns the socket lifecycle: creation and configuration, the single shared
 * connect() promise, and the connect_error branching that distinguishes auth
 * refusal from network failure. Auth errors trigger a single re-auth
 * redirect rather than socket.io back-off — the back-off loop would just
 * repeat the same failed credential. authz_denied additionally latches the
 * ReconnectPolicy and disables reconnection outright. auth_unavailable is the
 * exception that proves the rule: the credential was never judged, so back-off
 * is exactly right and neither the redirect nor the latch applies.
 */

import { io, Socket } from 'socket.io-client'
import { msalSocketAuth, parseAuthRejection, redirectForReauth } from './auth'
import { ReconnectPolicy } from './reconnect'
import { ConnectionLifecycle } from './connectionLifecycle'

export class WebSocketClient {
  private readonly lifecycle = new ConnectionLifecycle()
  private socket: Socket | null = null
  private isConnecting = false
  private connectionPromise: Promise<void> | null = null
  /** Event names with a shared fan-out listener attached to the current socket. */
  private attachedEvents = new Set<string>()

  constructor(
    private readonly policy: ReconnectPolicy,
    /** Invoked on every (re)connect so domain rooms can be rejoined. */
    private readonly onConnected: () => void
  ) {}

  /**
   * Connect to the backend Socket.IO server.
   *
   * Concurrent callers share one in-flight promise; an already-connected
   * socket resolves immediately. The socket is created once and reused for
   * later connect() calls (e.g. a manual retry after an auth refusal).
   */
  connect(): Promise<void> {
    if (this.socket?.connected) {
      return Promise.resolve()
    }

    if (this.connectionPromise) {
      return this.connectionPromise
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      const wsUrl = import.meta.env.VITE_WS_URL || 'http://localhost:8000'

      console.log('Connecting to WebSocket:', wsUrl)

      if (!this.socket) {
        this.socket = io(wsUrl, {
          path: '/api/v1/ws/',
          transports: ['websocket', 'polling'],
          timeout: 5000,
          forceNew: true,
          reconnection: true,
          reconnectionAttempts: this.policy.maxAttempts,
          reconnectionDelay: this.policy.baseDelayMs,
          auth: msalSocketAuth,
        })
        this.registerCoreHandlers()
      }

      const socket = this.socket

      const cleanup = () => {
        socket.off('connect', handleConnect)
        socket.off('connect_error', handleConnectError)
      }

      const handleConnect = () => {
        cleanup()
        this.connectionPromise = null
        console.log('WebSocket connected successfully')
        resolve()
      }

      const handleConnectError = (error: Error) => {
        const rejection = parseAuthRejection(error)

        if (rejection) {
          cleanup()
          this.connectionPromise = null

          if (rejection.code === 'authz_denied') {
            // Policy-level refusal — the principal is authenticated but lacks
            // the required role. Reconnecting with the same credential will
            // always fail, so stop the socket.io reconnect loop and surface
            // the awaiting-grant state.
            console.warn('WebSocket handshake refused: authz_denied — reconnection suppressed')
            socket.io.opts.reconnection = false
            this.policy.markAuthzDenied()
            reject(new Error('WebSocket auth rejected: authz_denied'))
            return
          }

          if (rejection.code === 'auth_unavailable') {
            // The server could not judge the credential — its datastore was
            // unreachable. The token is fine, so a re-auth redirect would bounce
            // every signed-in session for a fault that is not theirs; socket.io's
            // bounded back-off is the correct response and stays armed.
            console.warn('WebSocket handshake refused: auth_unavailable — retrying with back-off')
            reject(new Error('WebSocket auth rejected: auth_unavailable'))
            return
          }

          // auth_required | auth_invalid — trigger the re-auth redirect and
          // attempt a single reconnect with a freshly acquired token.
          console.warn('WebSocket auth rejected:', rejection.code)
          redirectForReauth()
          reject(new Error(`WebSocket auth rejected: ${rejection.code}`))
          return
        }

        // Network error — normal socket.io back-off reconnection handles it.
        if (this.policy.exhausted()) {
          cleanup()
          this.connectionPromise = null
          reject(new Error(`WebSocket connection failed after ${this.policy.maxAttempts} attempts`))
        }
        console.error('WebSocket connection error:', error.message)
      }

      socket.on('connect', handleConnect)
      socket.on('connect_error', handleConnectError)
      this.isConnecting = true
      socket.connect()
    })

    return this.connectionPromise
  }

  /** Register lifecycle handlers once per socket instance. */
  private registerCoreHandlers(): void {
    if (!this.socket) return
    const socket = this.socket

    this.socket.on('connect', () => {
      if (this.socket !== socket || this.lifecycle.isConnected()) return
      this.isConnecting = false
      this.policy.resetAttempts()
      this.onConnected()
      this.lifecycle.setConnected(true)
    })

    this.socket.on('disconnect', (reason: string) => {
      if (this.socket !== socket) return
      console.log('WebSocket disconnected:', reason)
      this.isConnecting = false
      this.lifecycle.setConnected(false)
    })

    this.socket.io.on('reconnect_attempt', (attempt: number) => {
      this.policy.recordAttempt(attempt)
      console.log(`WebSocket reconnect attempt ${attempt}/${this.policy.maxAttempts}`)
    })

    this.socket.io.on('reconnect_failed', () => {
      console.error(`Max reconnection attempts (${this.policy.maxAttempts}) reached`)
    })
  }

  hasSocket(): boolean {
    return this.socket !== null
  }

  isConnected(): boolean {
    return this.lifecycle.isConnected()
  }

  subscribeToConnectionStatus(callback: (connected: boolean) => void): () => void {
    return this.lifecycle.subscribeToStatus(callback)
  }

  subscribeToReconnect(callback: () => void): () => void {
    return this.lifecycle.subscribeToReconnect(callback)
  }

  getConnectionStats() {
    return {
      connected: this.isConnected(),
      reconnectAttempts: this.policy.currentAttempts,
      isConnecting: this.isConnecting,
      maxReconnectAttempts: this.policy.maxAttempts,
    }
  }

  /**
   * Attach a shared fan-out listener for a server event exactly once per
   * socket. Tracking is per event name; the set resets on disconnect so a
   * future socket gets fresh listeners.
   */
  attachOnce(event: string, handler: (...args: unknown[]) => void): void {
    if (!this.socket || this.attachedEvents.has(event)) return
    this.socket.on(event, handler)
    this.attachedEvents.add(event)
  }

  /**
   * Emit only while connected — room joins must not be buffered by
   * socket.io, because replay-on-reconnect already re-emits them.
   */
  emitWhenConnected(event: string, data: unknown): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data)
    }
  }

  /** Emit whenever a socket exists — room leaves fire even mid-reconnect. */
  emitIfSocket(event: string, data: unknown): void {
    this.socket?.emit(event, data)
  }

  /** Public passthrough emit — warns when no socket exists yet. */
  emit(event: string, data: unknown): void {
    if (!this.socket) {
      console.warn('WebSocket not connected. Cannot emit event:', event)
      return
    }
    this.socket.emit(event, data)
  }

  /** Public passthrough listener — returns a cleanup that detaches it. */
  on(event: string, callback: (data: unknown) => void): () => void {
    if (!this.socket) {
      console.warn('WebSocket not connected. Cannot subscribe to event:', event)
      return () => {}
    }

    const socket = this.socket
    socket.on(event, callback)

    return () => {
      socket.off(event, callback)
    }
  }

  /** Tear the socket down; domain registries are cleared by the service. */
  disconnect(): void {
    const socket = this.socket
    this.socket = null
    this.lifecycle.reset()
    if (socket) {
      console.log('Disconnecting WebSocket')
      socket.disconnect()
    }

    this.isConnecting = false
    this.connectionPromise = null
    this.attachedEvents.clear()
  }
}
